import { Icons, INewUserInvite, NewUserInvite, Organizations, SimpleRole, SimpleUser, useAuth, UserInvites, Users } from "@app/shared";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Input, InputLabel, InputRow, PageContainer, SearchInput, Select, UserRoleInput } from ".";
import { useMiniSearch } from "../../hooks/useMiniSearch";
import { DropDownMenu } from "./DropDownMenu";
import { IconLink } from "./IconLink";
import { CardPosition, SortableTable, SortableTableHeaderType } from "./SortableTable";
import { useConfirmDialog, useEditModelDialog, useFetch } from "../../hooks";
import { toastError, toastSuccess } from "../../helpers";

type UsersTableProps = {
    users: SimpleUser[] | undefined;
    isLoading?: boolean;
    onChange?: () => void;
}
type SimpleStatusUser = SimpleUser & { status: string };

export const UsersTable: React.FC<UsersTableProps> = ({ users, isLoading, onChange }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [allOrgs] = useFetch(Organizations.getAllOptions);
    const { currentOrganizationId } = useAuth();
    const [userInvites, , inviteHelpers] = useFetch(() => UserInvites.getUserInvites(currentOrganizationId), [currentOrganizationId]);
    const userInviteDialog = useEditModelDialog(NewUserInvite);
    const confirm = useConfirmDialog();
    const mergedUsers = useMemo(() => {
        const activeUsers = (users ?? []).map(u => {
            const us = u as SimpleStatusUser;
            us.status = 'Active';
            return us;
        });

        const invitedUsers = (userInvites ?? []).map(ui => ({
            firstName: ui.firstName,
            lastName: ui.lastName,
            email: ui.email,
            isSuperAdmin: false,
            id: ui.id,
            status: 'Invite Sent',
            userRoles: ui.roles?.map(uir => ({
                organizationId: ui.organizationId,
                orgName: ui.organizationName,
                name: uir
            } as SimpleRole))
        } as SimpleStatusUser));

        return [...activeUsers, ...invitedUsers];
    }, [users, userInvites]);

    const userDocs = useMemo(() => {
        return mergedUsers?.map(d => ({ ...d, userRoles: d.userRoles?.reduce((acc, current) => acc + ' ' + current.orgName + ' ' + current.name, '') }));
    }, [mergedUsers]);
    const fullTextFields: Array<keyof SimpleStatusUser> = ['email', 'firstName', 'lastName', 'userRoles'];
    const miniSearch = useMiniSearch(userDocs, fullTextFields, [userDocs]);

    const handleMakeSuper = async (user?: SimpleStatusUser) => {
        if (user?.status !== 'Active') { return; }
        Users.makeSuperUser(user.id).then(() => {
            toastSuccess(`${user.firstName} ${user.lastName} is a now a Super Admin`);
            onChange?.();
        }).catch((e: any) => {
            toastError(`Error upgrading to Super Admin: ${e}`);
        });
    }
    const handleRemoveSuper = (user: SimpleStatusUser) => {
        Users.removeSuperUser(user.id).then(() => {
            toastSuccess(`${user.firstName} ${user.lastName} is a no longer a Super Admin`);
            onChange?.();
        }).catch((e: any) => {
            toastError(`Error downgrading Super Admin: ${e}`);
        });
    }
    const handleRemoveUser = (user: SimpleStatusUser) => {
        confirm.show("Delete User?", `Are you sure you want to delete this user (${user.email})?`, "Delete", async () => {
            if (user.status === 'Active') {
                await Organizations.removeUser(currentOrganizationId, user.id);
                onChange?.();
            } else {
                await UserInvites.deleteInviteUser(userInvites?.find(x => x.id === user.id)!);
                inviteHelpers.refreshData();
            }
        });
    }
    const handleAddOnClick = () => {
        userInviteDialog.show(async (newUser: Partial<NewUserInvite>) => {
            await UserInvites.inviteUser(newUser as NewUserInvite);
            toastSuccess("Invite sent", "An invite has been sent to " + newUser.email);
            setTimeout(() => inviteHelpers.refreshData(), 1);
        }, `Invite New User`, new NewUserInvite({ organizationId: currentOrganizationId } as INewUserInvite));
    }
    const sortByOrg = (a: SimpleStatusUser, b: SimpleStatusUser) => {
        const nameA = a.userRoles?.[0]?.orgName;
        const nameB = b.userRoles?.[0]?.orgName;

        if (!nameA && !nameB) return 0;
        if (!nameA) return -1;
        if (!nameB) return 1;
        return nameA.localeCompare(nameB);
    }

    const columns: SortableTableHeaderType<SimpleStatusUser>[] = [
        {
            header: "First Name",
            dataKey: "firstName",
            width: 200
        },
        {
            header: "Last Name",
            dataKey: "lastName",
            width: 200
        },
        {
            header: "Email",
            dataKey: "email",
            width: 200
        },
        {
            header: "Status",
            dataKey: 'status',
            width: 150
        },
        {
            header: "Super Admin",
            dataKey: "isSuperAdmin",
            renderFunc: d => d?.isSuperAdmin ? <Icons.Success className="text-brand-primary" /> : null,
            width: 75,
            cardPosition: CardPosition.TopRight
        },
        {
            header: "Organization(s)",
            dataKey: "userRoles",
            renderFunc: d => [...new Set(d?.userRoles?.map(ur => ur.orgName))].join(', '),
            sortFunc: sortByOrg,
            width: 200
        },
        {
            header: "Roles",
            //dataKey: "userRoles",
            renderFunc: d => (<ul>{d?.userRoles?.sort((a: SimpleRole, b: SimpleRole) => a?.orgName?.localeCompare(b?.orgName ?? '') ?? 0)?.map((ur, idx) => (<li key={"ur" + idx}>{`${(!!ur.orgName) ? ur.orgName + ': ' : ''}${ur.name}`}</li>))}</ul>),
            width: 200
        },
        {
            header: "",
            width: 20,
            renderFunc: user => (
                <DropDownMenu>
                    {(!!!user?.isSuperAdmin) && <IconLink Icon={Icons.CaretUp} onClick={_ => handleMakeSuper(user)} >Make Super Admin</IconLink>}
                    {/* <div className="border-t text-brand-primary  !mt-6 !mb-4" /> */}
                    {user && (!!user.isSuperAdmin) && <IconLink Icon={Icons.CaretDown} onClick={_ => handleRemoveSuper(user)} >Remove Super Admin</IconLink>}
                    {user && <IconLink Icon={Icons.Trash} onClick={_ => handleRemoveUser(user)} >Delete</IconLink>}
                </DropDownMenu>),
        }
    ];

    const filteredData = useMemo(() => {
        if (!miniSearch || !searchParams.get("q")?.length) {
            return mergedUsers;
        }
        const searchResult = miniSearch.search(searchParams.get("q")!);
        const uniqueResults = Array.from(
            new Map(searchResult.map(item => [item.id, item])).values()
        );
        return uniqueResults.map(sr => mergedUsers?.find(x => x.id === sr.id) ?? {} as any);

    }, [searchParams, mergedUsers, miniSearch])
    const handleSearch = (terms: string | undefined | null) => {
        setSearchParams(existing => {
            if (terms) {
                existing.set("q", terms);
            } else {
                existing.delete("q");
            }
            return existing;
        })
    }
    return (
        <>
            <PageContainer title="All Site Users" titleRight={<IconLink Icon={Icons.PlusCircle} onClick={handleAddOnClick}>Add New</IconLink>}>
                <div className=" flex justify-end m-5">
                    <SearchInput onSearch={handleSearch} initialSearch={searchParams.get("q")} />
                </div>
                <SortableTable
                    isLoading={isLoading}
                    //onRowClick={handleOnRowClick}
                    initialSortKey={"email"}
                    columns={columns}
                    data={filteredData}
                    emptyValue={
                        <div className="my-5">
                            No users found
                        </div>
                    }
                />

                {searchParams.get("q") && <div className="m-3 flex justify-center font-semibold text-brand-primary"><h3 className="mx-auto">{`${filteredData?.length} results from query: '${searchParams.get('q')}'`}</h3></div>}
            </PageContainer>
            {userInviteDialog.renderDialog((userInvite, helpers) => (
                <>
                    <InputRow>
                        <Select label="Organization" options={allOrgs}  {...helpers.bindingsFor("organizationId")} />
                    </InputRow>
                    <InputRow>
                        <Input label="Email" {...helpers.bindingsFor("email")} />
                    </InputRow>
                    <InputRow>
                        <Input label="First Name" {...helpers.bindingsFor("firstName")} />
                    </InputRow>
                    <InputRow>
                        <Input label="Last Name" {...helpers.bindingsFor("lastName")} />
                    </InputRow>
                    <InputRow>
                        <InputLabel>Roles</InputLabel>
                        <UserRoleInput onChange={(roles) => helpers.updateModel("roles", roles)} value={userInvite.roles ?? []} />
                    </InputRow>
                </>
            ))}
            {confirm.renderDialog()}
        </>
    );
}
